import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS } from './tokens';
import { CrownPineRow } from './CrownPineRow';
import { A1ExpansionStudy } from './A1ExpansionStudy';
import { SiteLogisticsCard } from './SiteLogisticsCard';
import { CrownPineMap } from './CrownPineMap';
import { PortfolioSidePanel } from './PortfolioSidePanel';
import { PortfolioShareLinkModal, mastheadBtn, type ShareModalState } from './PortfolioPage';
import { GridContextPanel } from './GridContextPanel';
import { fetchPortfolio, type PortfolioPad, type PortfolioResponse } from '../../lib/portfolioApi';

interface Props {
  clientSlug: string; // always 'crownpine' — kept as a prop so the route stays generic
  token?: string;
}

type View = 'full' | 'shortlist' | 'inquiry';

// Bounding box [minLng, minLat, maxLng, maxLat] over a set of pads' geometries —
// used to frame the map on the Inquiry sites (which carry no server-side bbox).
function bboxOfPads(pads: PortfolioPad[]): [number, number, number, number] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      const [x, y] = c as number[];
      if (x < minX) minX = x; if (y < minY) minY = y;
      if (x > maxX) maxX = x; if (y > maxY) maxY = y;
    } else if (Array.isArray(c)) {
      for (const k of c) walk(k);
    }
  };
  for (const p of pads) if (p.geometry) walk((p.geometry as { coordinates?: unknown }).coordinates);
  if (!isFinite(minX)) return null;
  return [minX, minY, maxX, maxY];
}

// Merge several pads' Polygon/MultiPolygon geometries into one MultiPolygon so a
// multi-pad assembly draws as a single map feature.
function mergeGeometries(geoms: (GeoJSON.Geometry | null)[]): GeoJSON.Geometry | null {
  const polys: GeoJSON.Position[][][] = [];
  for (const g of geoms) {
    if (!g) continue;
    if (g.type === 'Polygon') polys.push(g.coordinates);
    else if (g.type === 'MultiPolygon') for (const poly of g.coordinates) polys.push(poly);
  }
  return polys.length ? { type: 'MultiPolygon', coordinates: polys } : null;
}

// Collapse the pads of one assembly (e.g. Site C's three blk-7 pads) into a
// single site entry: summed acreage, area-weighted slope/flood, merged geometry,
// and the assembly's logistics. Keeps the inquiry view at one row per site.
function combineAssembly(pads: PortfolioPad[], site: string, id: string): PortfolioPad {
  const acres = pads.reduce((s, p) => s + (p.acres ?? 0), 0) || null;
  const weighted = (pick: (p: PortfolioPad) => number | null): number | null => {
    let num = 0, den = 0;
    for (const p of pads) {
      const v = pick(p);
      if (v != null && p.acres != null) { num += v * p.acres; den += p.acres; }
    }
    return den > 0 ? Math.round((num / den) * 10) / 10 : null;
  };
  return {
    id,
    slug: pads[0].slug,
    piece_index: 0,
    site,
    acres,
    avg_slope_pct: weighted(p => p.avg_slope_pct),
    flood_pct: weighted(p => p.flood_pct),
    geometry: mergeGeometries(pads.map(p => p.geometry)),
    parent: pads[0].parent,
    logistics: pads.find(p => p.logistics)?.logistics,
  };
}

// Crown Pine research page — a standalone view built on the shared portfolio
// engine but with a briefing-style layout: a large map on the LEFT and the
// list / detail on the RIGHT. Deliberately NOT the Integra PortfolioPage
// (which stays frozen); it only reuses the leaf components (map, row, side panel).
//
// Three views: Full footprint (every contiguous clean pad ≥500 ac, ranked by
// its own acreage), the curated LRM top 10, and Inquiry sites (A1, A2, and C).
export const CrownPinePage: React.FC<Props> = ({ clientSlug, token }) => {
  if (!token && new URLSearchParams(window.location.search).get('view') === 'expansion') {
    return <A1ExpansionStudy />;
  }
  return <CrownPineResearchPage clientSlug={clientSlug} token={token} />;
};

const CrownPineResearchPage: React.FC<Props> = ({ clientSlug, token }) => {
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>(() => {
    const requested = new URLSearchParams(window.location.search).get('view');
    return requested === 'shortlist' || requested === 'inquiry' ? requested : 'full';
  });
  const [activePadId, setActivePadId] = useState<string | null>(null);
  const [expandedPadId, setExpandedPadId] = useState<string | null>(null);
  const [hoveredPadId, setHoveredPadId] = useState<string | null>(null);
  // Share-link modal (owner-only; hidden for token viewers) — reuses the
  // Integra portfolio's modal verbatim, POSTing to the same admin endpoint.
  const [shareModal, setShareModal] = useState<ShareModalState>({ open: false });

  // Measure the content row so the map fills the left column's height (the map
  // needs a pixel height; the ~46px covers its own toolbar + borders).
  const contentRef = useRef<HTMLDivElement>(null);
  const [mapH, setMapH] = useState(560);
  useLayoutEffect(() => {
    const measure = () => {
      const h = contentRef.current?.clientHeight;
      // Offset covers the map's toolbar row + the legend row beneath it + borders.
      if (h) setMapH(Math.max(360, h - 70));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [data]);

  useEffect(() => {
    if (expandedPadId && expandedPadId !== activePadId) setExpandedPadId(null);
  }, [activePadId, expandedPadId]);

  useEffect(() => {
    let mounted = true;
    fetchPortfolio(clientSlug, token)
      .then(d => { if (mounted) setData(d); })
      .catch(e => { if (mounted) setError(e.message || 'Failed to load'); });
    return () => { mounted = false; };
  }, [clientSlug, token]);

  // Full footprint: every computed clean pad EXCEPT the inquiry-only carve
  // tracts and curated LRM first-phase footprints. Selected sites (A/B/C) stay
  // pinned to the top, with the remaining pads ranked by acreage.
  const fullPads = useMemo(() => {
    if (!data?.pads) return [];
    const group = (p: PortfolioPad) => (p.site ? p.site.charCodeAt(0) - 64 : 99);
    return data.pads
      .filter(p => !p.inquiry_only && !p.shortlist)
      .slice()
      .sort((a, b) => group(a) - group(b) || (b.acres ?? -1) - (a.acres ?? -1));
  }, [data]);

  const shortlistPads = useMemo(() => {
    if (!data?.pads) return [];
    return data.pads
      .filter((p): p is PortfolioPad & { shortlist: NonNullable<PortfolioPad['shortlist']> } => p.shortlist != null)
      .slice()
      .sort((a, b) => a.shortlist.screen_rank - b.shortlist.screen_rank);
  }, [data]);

  // Inquiry sites: A1, A2 (the Site-A carve), then C — the full blk-7 assembly
  // (all three pads combined into one site: summed acreage, merged footprint).
  const inquirySites = useMemo(() => {
    if (!data?.pads) return [];
    const a1 = data.pads.find(p => p.site === 'A1');
    const a2 = data.pads.find(p => p.site === 'A2');
    const cPads = data.pads.filter(p => p.site === 'C');
    const c = cPads.length ? combineAssembly(cPads, 'C', 'crownpine-blk-7#C') : undefined;
    return [a1, a2, c].filter((p): p is PortfolioPad => p != null);
  }, [data]);

  const inquiryBbox = useMemo(() => bboxOfPads(inquirySites), [inquirySites]);
  // In the Inquiry view the map shows only the two parent tract dots (blk-194,
  // blk-7) instead of all ~30 blocks, so the three sites read clearly.
  const inquiryRows = useMemo(() => {
    if (!data) return [];
    const pids = new Set(inquirySites.map(p => p.parent?.parcel_id).filter(Boolean));
    return data.rows.filter(r => pids.has(r.parcel_id));
  }, [data, inquirySites]);
  // In the shortlist view the outline is the screened opportunity block, not
  // the representative database parcel. The selected first-phase campus is a
  // separate pad layer, making land estate and phase-one footprint legible at
  // the same time.
  const shortlistRows = useMemo(() => {
    if (!data) return [];
    const rowsByParcel = new Map(data.rows.map(row => [row.parcel_id, row]));
    return shortlistPads.flatMap(pad => {
      const parcelId = pad.parent?.parcel_id;
      const row = parcelId ? rowsByParcel.get(parcelId) : undefined;
      if (!row) return [];
      return [{
        ...row,
        rank: pad.shortlist.screen_rank,
        site_label: pad.shortlist.block_id,
        priority_label: pad.shortlist.tier,
        key_numbers: {
          ...row.key_numbers,
          acres: pad.shortlist.gross_legal_acres,
          available_mw: null,
        },
        knockouts: {
          ...row.knockouts,
          count: pad.shortlist.tier === 'Advance' ? 0 : 1,
          list: [],
        },
        score: null,
        grade: null,
        geometry: pad.shortlist.block_geometry,
        member_geometries: null,
      }];
    });
  }, [data, shortlistPads]);
  const hasInquiry = inquirySites.length > 0;
  const hasShortlist = shortlistPads.length > 0;
  useEffect(() => {
    if (!data) return;
    if ((view === 'shortlist' && !hasShortlist) || (view === 'inquiry' && !hasInquiry)) {
      setView('full');
      setActivePadId(null);
      setExpandedPadId(null);
      setHoveredPadId(null);
    }
  }, [data, view, hasShortlist, hasInquiry]);
  const fullRows = useMemo(() => {
    if (!data) return [];
    const shortlistParcelIds = new Set(shortlistPads.map(pad => pad.parent?.parcel_id).filter(Boolean));
    return data.rows.filter(row => !shortlistParcelIds.has(row.parcel_id));
  }, [data, shortlistPads]);
  const shortlistBbox = useMemo(() => bboxOfPads(shortlistPads.map(pad => ({
    ...pad,
    geometry: pad.shortlist.block_geometry,
  }))), [shortlistPads]);
  const mapPads = view === 'inquiry' ? inquirySites : view === 'shortlist' ? shortlistPads : fullPads;
  const mapRows = view === 'inquiry' ? inquiryRows : view === 'shortlist' ? shortlistRows : fullRows;
  const mapBbox = view === 'inquiry'
    ? (inquiryBbox ?? data?.synthesis?.bbox ?? null)
    : view === 'shortlist'
      ? (shortlistBbox ?? data?.synthesis?.bbox ?? null)
    : (data?.synthesis?.bbox ?? null);

  // Switching view drops any stale selection so the map/side panel don't point
  // at a pad that isn't in the current list.
  const switchView = (v: View) => {
    if (v === view) return;
    setView(v);
    setActivePadId(null);
    setExpandedPadId(null);
    setHoveredPadId(null);
  };

  const cycleScores = useMemo(
    () => (data ? data.rows.map(r => r.score).filter((s): s is number => s != null) : []),
    [data]
  );

  // The active pad's parent block drives the map's tract-dot ring + the side
  // panel (which is a per-block briefing summary, not a per-pad view).
  const activePad = useMemo(
    () => (data?.pads && activePadId ? data.pads.find(p => p.id === activePadId) ?? null : null),
    [data, activePadId]
  );
  const activeParcelId = activePad?.parent?.parcel_id ?? null;
  const expandedRow = useMemo(() => {
    if (!data?.pads || !expandedPadId) return null;
    // Inquiry view's Site C is a synthetic combined pad (`crownpine-blk-7#C`)
    // that doesn't exist in data.pads — resolve it via inquirySites.
    const pad = data.pads.find(p => p.id === expandedPadId)
      ?? inquirySites.find(p => p.id === expandedPadId);
    if (!pad?.parent) return null;
    return data.rows.find(r => r.parcel_id === pad.parent!.parcel_id) ?? null;
  }, [data, expandedPadId, inquirySites]);
  // The expanded selection's pads, for the side panel's per-pad acreage list.
  // Inquiry view: the SITE's own pads (A1/A2 are inquiry_only; C = 3 blk-7
  // pads). Full view: the block's ranked pads (inquiry carves excluded so
  // A1/A2 don't double-count against blk-194's own pieces).
  const expandedRowPads = useMemo(() => {
    if (!data?.pads || !expandedPadId || !expandedRow) return undefined;
    if (view === 'shortlist') {
      const pad = shortlistPads.find(candidate => candidate.id === expandedPadId);
      return pad ? [pad] : undefined;
    }
    if (view === 'inquiry') {
      const site = inquirySites.find(p => p.id === expandedPadId)?.site
        ?? data.pads.find(p => p.id === expandedPadId)?.site;
      if (site) {
        const sitePads = data.pads.filter(p => p.site === site);
        if (sitePads.length) return sitePads;
      }
    }
    return data.pads.filter(p => !p.inquiry_only && p.parent?.parcel_id === expandedRow.parcel_id);
  }, [data, expandedPadId, expandedRow, view, inquirySites, shortlistPads]);

  // Clicking a tract dot (a block) selects that block's largest pad in view.
  const selectPadForParcel = (parcelId: string) => {
    const pad = mapPads.find(p => p.parent?.parcel_id === parcelId);
    if (pad) setActivePadId(pad.id);
  };

  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  const handleOpenBriefing = (parcelId: string) => window.open(`/briefing/${parcelId}${tokenParam}`, '_blank', 'noopener,noreferrer');
  const handleOpenMemo = (parcelId: string) => window.open(`/briefing/${parcelId}/v2${tokenParam}`, '_blank', 'noopener,noreferrer');

  useEffect(() => {
    if (!activePadId) return;
    const el = document.querySelector(`[data-parcel-row-id="${CSS.escape(activePadId)}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activePadId]);

  if (error) {
    return (
      <div style={{ padding: 80, textAlign: 'center', fontFamily: PORTFOLIO_TOKENS.sans, color: REPORT.body }}>
        <h1 style={{ fontFamily: PORTFOLIO_TOKENS.serif, color: REPORT.heading, fontWeight: 400 }}>Pine Curtain research unavailable</h1>
        <p style={{ marginTop: 12, color: REPORT.secondary }}>{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 80, textAlign: 'center', fontFamily: PORTFOLIO_TOKENS.mono, color: REPORT.tertiary, fontSize: 11, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
        Loading Pine Curtain…
      </div>
    );
  }

  return (
    <div style={{
      background: REPORT.white,
      height: '100vh',
      overflow: 'hidden',
      fontFamily: PORTFOLIO_TOKENS.sans,
      color: REPORT.body,
      fontSize: 12.5,
      lineHeight: 1.5,
      padding: '12px 20px 0',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Masthead — Atwater mark only (NO Integra co-brand; this is internal
          research, not the client deliverable). */}
      <header style={{
        background: REPORT.heading,
        borderRadius: 4,
        padding: '9px 18px',
        marginBottom: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
          <img src="/atwater-logo-light.svg" alt="Atwater Energy" style={{ height: 26, width: 'auto', display: 'block' }} />
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 17, color: '#fff', whiteSpace: 'nowrap',
          }}>
            Pine Curtain
          </span>
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.5, fontWeight: 500, letterSpacing: '0.10em',
            textTransform: 'uppercase', color: PORTFOLIO_TOKENS.patina, whiteSpace: 'nowrap',
            borderLeft: `1px solid rgba(212,152,128,0.28)`, paddingLeft: 14,
          }}>
            East Texas play · 9 counties
          </span>
        </div>
        {/* Share button — owner-only (token viewers can't mint more links). */}
        {!token && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <a href="/portfolio/crownpine?view=expansion" style={{ ...mastheadBtn, textDecoration: 'none' }}>A1 expansion</a>
            <button type="button" style={mastheadBtn} onClick={() => setShareModal({ open: true })}>
              Share
            </button>
          </div>
        )}
      </header>

      {shareModal.open && (
        <PortfolioShareLinkModal
          clientSlug={clientSlug}
          state={shareModal}
          onChange={setShareModal}
          onClose={() => setShareModal({ open: false })}
        />
      )}

      {/* Two-column body: map left (large), list/detail right. */}
      <div ref={contentRef} style={{ flex: 1, minHeight: 0, display: 'flex', gap: 14, paddingBottom: 12 }}>
        {/* MAP — left, dominant. */}
        <div style={{ flex: '1 1 62%', minWidth: 0 }}>
          <CrownPineMap
            rows={mapRows}
            pads={mapPads}
            cycleBbox={mapBbox}
            onSelect={selectPadForParcel}
            onSelectPad={setActivePadId}
            onHover={() => {}}
            onHoverPad={setHoveredPadId}
            activeParcelId={activeParcelId}
            activePadId={activePadId}
            focusPadId={expandedPadId}
            hoveredPadId={hoveredPadId}
            sticky={false}
            height={mapH}
            shareToken={token}
            ownerParcels
            pipelineFamilyStyling
            pipelineUnfiltered
          />
        </div>

        {/* LIST / DETAIL — right, scrollable. */}
        <div style={{ flex: '1 1 38%', minWidth: 360, maxWidth: 560, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* View toggle — computed footprint, curated LRM shortlist, inquiry sites. */}
          {(hasShortlist || hasInquiry) && (
            <div style={{ display: 'flex', flexShrink: 0, marginBottom: 8, border: `1px solid ${REPORT.rule}`, borderRadius: 4, overflow: 'hidden' }}>
              {([
                ['full', 'Full footprint', true],
                ['shortlist', `LRM shortlist · ${shortlistPads.length}`, hasShortlist],
                ['inquiry', 'Inquiry sites · A1 · A2 · C', hasInquiry],
              ] as const).filter(([, , available]) => available).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => switchView(v)}
                  style={{
                    flex: 1, padding: '7px 8px', border: 'none', cursor: 'pointer',
                    fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.3, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    background: view === v ? REPORT.heading : REPORT.white,
                    color: view === v ? '#fff' : REPORT.secondary,
                    transition: 'background 0.12s ease', whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div style={{
            flexShrink: 0, fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, letterSpacing: '0.06em',
            color: REPORT.tertiary, padding: '0 4px 8px', lineHeight: 1.5,
          }}>
            {view === 'inquiry'
              ? 'The three land-inquiry sites. Site A is carved into two contiguous tracts — A1 (5 parcels on US Hwy 69) and A2 (3 parcels due-south, dependent on A1). Each pitch site carries drive times, airport access, and grid tie-in.'
              : view === 'shortlist'
                ? 'Curated LRM screen. The 273–605 ac polygons are first-phase footprints (~1 GW screening), not the whole 3–5+ GW land estate. Gross block acreage and expansion status are shown separately. Fiber carries zero weight in this ranking.'
              : 'Each row is one contiguous buildable pad ≥500 ac (outside FEMA-100yr ∪ NWI wetlands), ranked by its own acreage across all Crown Pine land — a block with many pads appears as many rows. Missing slope or flood values remain unscreened; pending FEMA is labeled separately.'}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', paddingRight: 4, paddingBottom: 24 }}>
            {view === 'full' && data.grid_context && <GridContextPanel grid={data.grid_context} />}

            {view === 'inquiry' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {inquirySites.map((pad, i) => (
                  <div key={pad.id}>
                    <CrownPineRow
                      pad={pad}
                      rowNumber={i + 1}
                      isActive={pad.id === activePadId}
                      isHovered={pad.id === hoveredPadId}
                      isExpanded={pad.id === expandedPadId}
                      onSelect={setActivePadId}
                      onHover={setHoveredPadId}
                      onToggleExpand={(pid) => setExpandedPadId(prev => (prev === pid ? null : pid))}
                    />
                    {pad.logistics && <SiteLogisticsCard logistics={pad.logistics} />}
                  </div>
                ))}
              </div>
            ) : view === 'shortlist' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {shortlistPads.map((pad, i) => (
                  <CrownPineRow
                    key={pad.id}
                    pad={pad}
                    rowNumber={i + 1}
                    isActive={pad.id === activePadId}
                    isHovered={pad.id === hoveredPadId}
                    isExpanded={pad.id === expandedPadId}
                    onSelect={setActivePadId}
                    onHover={setHoveredPadId}
                    onToggleExpand={(pid) => setExpandedPadId(prev => (prev === pid ? null : pid))}
                  />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {fullPads.map((pad, i) => {
                  const prev = i > 0 ? fullPads[i - 1] : null;
                  const header =
                    i === 0 && pad.site ? 'Selected sites — land inquiry' :
                    pad.site == null && (i === 0 || prev?.site != null) ? 'All pads — ranked by acreage' :
                    null;
                  return (
                    <React.Fragment key={pad.id}>
                      {header && (
                        <div style={{
                          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, fontWeight: 700,
                          letterSpacing: '0.10em', textTransform: 'uppercase', color: REPORT.tertiary,
                          padding: i === 0 ? '2px 4px 2px' : '14px 4px 2px',
                          borderBottom: `1px solid ${REPORT.rule}`,
                        }}>
                          {header}
                        </div>
                      )}
                      <CrownPineRow
                        pad={pad}
                        rowNumber={i + 1}
                        isActive={pad.id === activePadId}
                        isHovered={pad.id === hoveredPadId}
                        isExpanded={pad.id === expandedPadId}
                        onSelect={setActivePadId}
                        onHover={setHoveredPadId}
                        onToggleExpand={(pid) => setExpandedPadId(prev => (prev === pid ? null : pid))}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <PortfolioSidePanel
        row={expandedRow}
        cycleScores={cycleScores}
        onClose={() => setExpandedPadId(null)}
        onOpenBriefing={handleOpenBriefing}
        onOpenMemo={handleOpenMemo}
        pads={expandedRowPads}
      />
    </div>
  );
};

export default CrownPinePage;
